import { useState } from 'react';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  DialogTrigger,
  Popover,
  SearchField,
  Text,
} from '@backstage/ui';
import { RiArrowDownSLine } from '@remixicon/react';

import { ClearItemsButton } from '../ClearItemsButton/ClearItemsButton';
import styles from './FilterDropdown.module.css';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterDropdownProps {
  /** Text of the trigger button; its accessible name is `${label} filter`. */
  label: string;
  filterOptions: FilterOption[];
  /** Called with all selected values after every change. */
  onChange: (selectedOptions: string[]) => void;
  /** Called when the count pill next to the trigger clears the selection. */
  onClear: () => void;
  /**
   * Selected values. The dropdown follows changes from outside (a reset, URL
   * navigation); an array with the same values counts as unchanged.
   */
  selectedValues?: string[];
  /** Adds a search field above the options that filters them by label. */
  isSearchEnabled?: boolean;
  shouldSortByLabel?: boolean;
  className?: string;
}

const sameValues = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * Multi-select filter for a filter bar: a quiet trigger button that opens a
 * popover with checkboxes, and a pill with the number of selected values
 * that clears them.
 *
 * @example
 * <FilterDropdown
 *   label="Owner"
 *   filterOptions={[{ label: 'Team A', value: 'group:default/team-a' }]}
 *   selectedValues={selected}
 *   isSearchEnabled
 *   onChange={setSelected}
 *   onClear={() => setSelected([])}
 * />
 */
export const FilterDropdown = ({
  label,
  filterOptions,
  onChange,
  onClear,
  selectedValues = [],
  isSearchEnabled = false,
  shouldSortByLabel = false,
  className,
}: FilterDropdownProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOptions, setSelectedOptions] = useState(selectedValues);
  const [syncedValues, setSyncedValues] = useState(selectedValues);

  // Callers pass a new array on every render, so compare by content
  if (!sameValues(selectedValues, syncedValues)) {
    setSyncedValues(selectedValues);
    setSelectedOptions(selectedValues);
  }

  const handleChange = (values: string[]) => {
    setSelectedOptions(values);
    onChange(values);
  };

  const handleClear = () => {
    setSelectedOptions([]);
    onClear();
  };

  const optionsToDisplay = shouldSortByLabel
    ? [...filterOptions].sort((a, b) => a.label.localeCompare(b.label))
    : filterOptions;

  const query = isSearchEnabled ? searchQuery.trim().toLowerCase() : '';
  const filteredOptions = query
    ? optionsToDisplay.filter(item => item.label.toLowerCase().includes(query))
    : optionsToDisplay;

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <DialogTrigger
        onOpenChange={isOpen => {
          if (!isOpen) {
            setSearchQuery('');
          }
        }}
      >
        <Button
          variant="tertiary"
          size="small"
          aria-label={`${label} filter`}
          iconEnd={<RiArrowDownSLine aria-hidden />}
          className={styles.trigger}
        >
          {label}
        </Button>
        <Popover placement="bottom start" hideArrow className={styles.popover}>
          {isSearchEnabled && (
            <div className={styles.search}>
              <SearchField
                size="small"
                // Focus moves into the popover that was just opened
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                aria-label={`Search ${label}`}
                placeholder="Search"
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>
          )}
          {filteredOptions.length > 0 ? (
            <CheckboxGroup
              aria-label={label}
              value={selectedOptions}
              onChange={handleChange}
              className={styles.options}
            >
              {filteredOptions.map(item => (
                <Checkbox
                  key={item.value}
                  value={item.value}
                  className={styles.option}
                >
                  {item.label}
                </Checkbox>
              ))}
            </CheckboxGroup>
          ) : (
            <Text as="p" color="secondary" className={styles.empty}>
              No results found.
            </Text>
          )}
        </Popover>
      </DialogTrigger>
      {selectedOptions.length > 0 && (
        <ClearItemsButton
          activeItems={`${selectedOptions.length}`}
          aria-label={`Clear the ${label} filter`}
          onClear={handleClear}
        />
      )}
    </div>
  );
};
